import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { liveRatesService } from '../services/liveRatesService';

interface EnterAmountPageProps {
  customerData?: {
    fullName: string;
    mobileNumber: string;
  };
  onAmountSubmit?: (data: { amount: number; quantity: number; donationType: string }) => void;
}

const EnterAmountPage: React.FC<EnterAmountPageProps> = ({ 
  customerData: propCustomerData, 
  onAmountSubmit 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get customer data from navigation state or props
  const customerData = location.state?.customerData || propCustomerData;
  const [formData, setFormData] = useState({
    donationMode: 'rupee', // 'rupee' or 'grams'
    amount: '',
    quantity: '',
    donationType: 'General Temple Donation',
    message: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);
  /** Buy rate per gram from API — same field used for payments (see razorpay routes). */
  const [goldBuyRate, setGoldBuyRate] = useState<number | null>(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [ratesError, setRatesError] = useState<string | null>(null);

  const loadGoldRate = useCallback(async () => {
    setRatesLoading(true);
    setRatesError(null);
    const res = await liveRatesService.getCurrentRates();
    if (res.success && res.data?.gold_buy_rate != null && res.data.gold_buy_rate > 0) {
      setGoldBuyRate(res.data.gold_buy_rate);
    } else {
      setGoldBuyRate(null);
      setRatesError(res.error || '');
    }
    setRatesLoading(false);
  }, []);

  useEffect(() => {
    loadGoldRate();
    const interval = setInterval(loadGoldRate, 60_000);
    return () => clearInterval(interval);
  }, [loadGoldRate]);

  useEffect(() => {
    if (goldBuyRate == null || goldBuyRate <= 0) return;
    setFormData((prev) => {
      if (prev.donationMode === 'rupee' && prev.amount) {
        const amount = parseFloat(prev.amount);
        if (!isNaN(amount) && amount > 0) {
          return { ...prev, quantity: (amount / goldBuyRate).toFixed(4) };
        }
      }
      if (prev.donationMode === 'grams' && prev.quantity) {
        const quantity = parseFloat(prev.quantity);
        if (!isNaN(quantity) && quantity > 0) {
          return { ...prev, amount: (quantity * goldBuyRate).toFixed(2) };
        }
      }
      return prev;
    });
  }, [goldBuyRate]);

  // Quick amount options
  const quickAmounts = [500, 1000, 2500, 5000, 10000, 25000];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-calculate quantity when amount changes (only in rupee mode)
    if (name === 'amount' && value && formData.donationMode === 'rupee' && goldBuyRate != null && goldBuyRate > 0) {
      const amount = parseFloat(value);
      if (!isNaN(amount) && amount > 0) {
        const calculatedQuantity = (amount / goldBuyRate).toFixed(4);
        setFormData(prev => ({
          ...prev,
          quantity: calculatedQuantity
        }));
      }
    }

    // Auto-calculate amount when quantity changes (only in grams mode)
    if (name === 'quantity' && value && formData.donationMode === 'grams' && goldBuyRate != null && goldBuyRate > 0) {
      const quantity = parseFloat(value);
      if (!isNaN(quantity) && quantity > 0) {
        const calculatedAmount = (quantity * goldBuyRate).toFixed(2);
        setFormData(prev => ({
          ...prev,
          amount: calculatedAmount
        }));
      }
    }
  };

  const handleDonationModeChange = (mode: 'rupee' | 'grams') => {
    setFormData(prev => ({
      ...prev,
      donationMode: mode,
      amount: '',
      quantity: ''
    }));
    setErrors({});
  };

  const handleQuickAmountSelect = (amount: number) => {
    if (goldBuyRate == null || goldBuyRate <= 0) return;
    setFormData(prev => ({
      ...prev,
      amount: amount.toString(),
      quantity: (amount / goldBuyRate).toFixed(4)
    }));
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (formData.donationMode === 'rupee') {
      if (!formData.amount || parseFloat(formData.amount) < 10) {
        newErrors.amount = t('pages.enterAmount.errors.amountGreaterThan10') as string;
      }
    } else {
      if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
        newErrors.quantity = t('pages.enterAmount.errors.validQuantity') as string;
      }
    }

    if (!formData.donationType.trim()) {
      newErrors.donationType = t('pages.enterAmount.errors.selectDonationType') as string;
    }

    if (!termsAccepted) {
      newErrors.termsAccepted = t('pages.enterAmount.errors.acceptTerms') as string;
    }

    if (!ratesLoading && (goldBuyRate == null || goldBuyRate <= 0)) {
      newErrors.rates = (ratesError || (t('pages.enterAmount.errors.rateUnavailable') as string));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceedToPayment = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // No need for artificial delay - proceed immediately
      if (goldBuyRate == null || goldBuyRate <= 0) {
        return;
      }
      const amountData = {
        amount: parseFloat(formData.amount),
        quantity: parseFloat(formData.quantity),
        donationType: formData.donationType,
        ratePerGram: goldBuyRate
      };

      if (onAmountSubmit) {
        onAmountSubmit(amountData);
      } else {
        // Navigate to payment gateway with amount data
        navigate('/payment-gateway', { 
          state: { 
            customerData,
            amountData 
          } 
        });
      }
    } catch (error) {
      console.error('Error proceeding to payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToVerification = () => {
    navigate('/verification');
  };

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full border-2 border-white flex items-center justify-center">
                <img 
                  src="/images/Asset2.png" 
                  alt="Temple Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{t('pages.enterAmount.title')}</h1>
            <p className="text-white/90 text-xs sm:text-sm mt-1">{t('pages.enterAmount.subtitle')}</p>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Customer Info - Simple Display */}
            {customerData && (
              <div className="text-center mb-3 sm:mb-4">
                <p className="text-gray-800 text-xs sm:text-sm font-bold">
                  <span className="font-bold">{customerData.fullName}</span> • +91 {customerData.mobileNumber}
                </p>
              </div>
            )}


            {/* Donation Mode Selection */}
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-2 sm:mb-3">{t('pages.enterAmount.donationMode')}</label>
              <div className="flex space-x-2 sm:space-x-3">
                <button
                  type="button"
                  onClick={() => handleDonationModeChange('rupee')}
                  className={`flex-1 flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 rounded-lg border-2 transition-colors text-xs sm:text-sm ${
                    formData.donationMode === 'rupee'
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-white border-orange-500 text-orange-500'
                  }`}
                >
                  <span className="mr-1 sm:mr-2">💰</span>
                  {t('pages.enterAmount.byRupeeAmount')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDonationModeChange('grams')}
                  className={`flex-1 flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 rounded-lg border-2 transition-colors text-xs sm:text-sm ${
                    formData.donationMode === 'grams'
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-white border-orange-500 text-orange-500'
                  }`}
                >
                  <span className="mr-1 sm:mr-2">⚖️</span>
                  {t('pages.enterAmount.byGoldGrams')}
                </button>
              </div>
            </div>

            {/* Quick Amount Selection */}
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-2 sm:mb-3">{t('pages.enterAmount.quickAmountSelection')}</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                {quickAmounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    disabled={ratesLoading || goldBuyRate == null || goldBuyRate <= 0}
                    onClick={() => handleQuickAmountSelect(amount)}
                    className="px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors font-medium text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ₹{amount.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-4 sm:space-y-6">
              {/* Amount/Quantity Input */}
              {formData.donationMode === 'rupee' ? (
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-2">
                    {t('pages.enterAmount.rupeeAmount')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-400 text-xs sm:text-sm">₹</span>
                    </div>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                    placeholder={t('pages.enterAmount.enterAmountRupees') as string}
                    step="0.01"
                    min="10.01"
                      className={`w-full pl-7 sm:pl-8 pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-sm sm:text-base ${
                        errors.amount ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.amount && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.amount}</p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-2">
                    {t('pages.enterAmount.goldQuantity')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-400 text-xs sm:text-sm">g</span>
                    </div>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      placeholder={t('pages.enterAmount.enterQuantityGrams') as string}
                      step="0.0001"
                      min="0"
                      className={`w-full pl-4 pr-7 sm:pr-8 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-sm sm:text-base ${
                        errors.quantity ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.quantity && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.quantity}</p>
                  )}
                </div>
              )}

              {/* Donation Type */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-2">
                  {t('pages.enterAmount.donationType')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400 text-sm">❤️</span>
                  </div>
                  <select
                    name="donationType"
                    value={formData.donationType}
                    onChange={handleInputChange}
                    className="w-full pl-8 sm:pl-10 pr-8 sm:pr-10 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors appearance-none text-sm sm:text-base"
                  >
                    <option value="General Temple Donation">{t('pages.enterAmount.donationTypes.general')}</option>
                    <option value="Special Pooja Donation">{t('pages.enterAmount.donationTypes.specialPooja')}</option>
                    <option value="Festival Donation">{t('pages.enterAmount.donationTypes.festival')}</option>
                    <option value="Maintenance Donation">{t('pages.enterAmount.donationTypes.maintenance')}</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {errors.donationType && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.donationType}</p>
                )}
              </div>

              {/* Message (Optional) */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-2">
                  {t('pages.enterAmount.messageOptional')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t('pages.enterAmount.messagePlaceholder') as string}
                  rows={3}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none text-sm sm:text-base"
                />
              </div>

              {/* Gold Rate Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-yellow-800 font-medium text-xs sm:text-sm">{t('pages.enterAmount.currentGoldRate')}:</span>
                  {ratesLoading ? (
                    <span className="text-yellow-800 font-medium text-xs sm:text-sm">{t('pages.enterAmount.loadingGoldRate')}</span>
                  ) : goldBuyRate != null && goldBuyRate > 0 ? (
                    <span className="text-yellow-800 font-bold text-xs sm:text-sm">₹{goldBuyRate.toFixed(2)}/gram</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { void loadGoldRate(); }}
                      className="text-left text-yellow-900 font-medium text-xs sm:text-sm underline"
                    >
                      {(ratesError || (t('pages.enterAmount.errors.rateUnavailable') as string))}
                    </button>
                  )}
                </div>
                {errors.rates && (
                  <p className="mt-2 text-xs text-red-600">{errors.rates}</p>
                )}
                {formData.amount && formData.quantity && goldBuyRate != null && goldBuyRate > 0 && (
                  <div className="mt-2 text-xs sm:text-sm text-yellow-700">
                    {formData.donationMode === 'rupee' 
                      ? t('pages.enterAmount.equivalentGrams', { quantity: formData.quantity })
                      : t('pages.enterAmount.equivalentRupees', { amount: formData.amount })
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="mb-4 sm:mb-6">
              <label className="flex items-start space-x-2 sm:space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (errors.termsAccepted) {
                      setErrors(prev => ({
                        ...prev,
                        termsAccepted: ''
                      }));
                    }
                  }}
                  className="mt-1 w-4 h-4 sm:w-5 sm:h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                />
                <span className="text-xs sm:text-sm text-gray-700">
                  {t('pages.enterAmount.acceptTerms')}{' '}
                  <Link
                    to="/terms-and-conditions"
                    className="text-orange-500 hover:text-orange-600 underline font-semibold"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {t('pages.enterAmount.termsAndConditions')}
                  </Link>
                  <span className="text-red-500">*</span>
                </span>
              </label>
              {errors.termsAccepted && (
                <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.termsAccepted}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 sm:space-y-3">
              <button
                onClick={handleProceedToPayment}
                disabled={
                  loading ||
                  ratesLoading ||
                  goldBuyRate == null ||
                  goldBuyRate <= 0 ||
                  (!formData.amount && !formData.quantity)
                }
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm sm:text-base"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                ) : null}
                {t('pages.enterAmount.donate')} ₹{formData.amount || '0.00'}
              </button>
              
              <button
                onClick={handleBackToVerification}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors text-sm sm:text-base"
              >
                {t('pages.enterAmount.backToVerification')}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4 sm:mt-6 md:mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterAmountPage;
